import React, { useEffect, useState } from "react";
import {
	Row,
	Col,
	CardTitle,
	Label,
	CardBody,
	CardHeader,
	Form,
	CardText,
	Button,
} from "reactstrap";
import NumberSelector from "features/inventory/subFeatures/selector/NumberSelector";
import FinanceTermInput from "../finance/FinanceTermInput";
import useCurrentSaleStore from "stores/SaleStore";
import LeaseRollModalBody from "./LeaseRollModalBody";
import LeaseRollDriveOffModalBody from "./LeaseRollDriveOffModalBody";
import useCurrentDealStore from "stores/DealStore";
import { DealStatus, LoanPeriod } from "constants/Constants";
import OpenModal from "./OpenModal";
import formNavigationUtils from "utils/formNavigationUtils";
import useCurrentRecapStore from "stores/RecapStore";
import shallow from "zustand/shallow";
import { confirmDeleteMessage } from "utils/alertUtils";
import BalloonModalBody from "./BalloonModalBody";
import FinanceDaysToPayInput from "../finance/FinanceDaysToPayInput";
import ResidualInput from "../lease/ResidualInput";
import MoneyFacInput from "../lease/MoneyFacInput";
import useCurrentLeaseStore from "../../../../stores/LeaseStore";
import MarkupInput from "../lease/MarkupInput";
import LeaseAprInput from "../lease/LeaseAprInput";
import { moneyFactorFromApr } from "../../helperFunctions/LeaseFunctions";
import PaymentOptionsModal from "./PaymentOptionsModal";
import ChildrenModal from "components/modals/ChildrenModal";
import OpenModalButton from "features/deal/components/OpenModalButton";

/**
 * TermsTable component to display the terms and payment options.
 *
 * @returns {JSX.Element} The rendered component
 */
const LeaseTerms = () => {
	const [showProfit, setShowProfit] = useState(false);
	const [paymentOptions, setPaymentOptions] = useState(false);

	const toggle = () => setShowProfit(!showProfit);
	const togglePayment = () => setPaymentOptions(!paymentOptions);

	const {
		leasePeriod,
		daysToPay,
		firstDueDate,
		loanDate,
		term,
		residualFactor,
		leaseType,
		editField,
	} = useCurrentLeaseStore(
		(state) => ({
			...state,
			apr: state.apr,
		}),
		shallow
	);
	console.log(residualFactor);
	// const lease = useCurrentLeaseStore((state) => ({ ...state }), shallow);

	const { netProfit } = useCurrentRecapStore((state) => state, shallow);

	const { type, dealId } = useCurrentDealStore(
		(state) => ({
			type: state.deal.type,
			dealId: state.deal.ID,
		}),
		shallow
	);

	const [menuSelling, setMenuSelling] = useState(false);
	const toggleSelling = () => setMenuSelling(!menuSelling);

	return (
		<>
			<CardHeader>
				<CardTitle tag="h3">Payments</CardTitle>
			</CardHeader>
			<CardBody className="pb-0" onKeyDown={formNavigationUtils}>
				<Form className="deal-terms-table">
					<Row className="mx-0">
						<Col xl="5" className="mb-2">
							<Label className="">Period</Label>
							<NumberSelector
								name="dealLoanPeriod"
								readOnly={type !== DealStatus.PENDING}
								value={leasePeriod}
								selectedOption={leasePeriod}
								handleChange={(e) => editField("leasePeriod", e)}
								selectType={"loanPeriod"}
								default={true}
							/>
						</Col>
						<Col xl="3" className="mb-2">
							<Label>Term</Label>
							<FinanceTermInput
								loanPeriod={leasePeriod}
								term={term}
								editTerm={(e) => editField("term", e)}
							/>
						</Col>
						<Col xl="3" className="mb-2">
							<Label>Days to 1st Pmt</Label>
							<FinanceDaysToPayInput
								loanPeriod={leasePeriod}
								daysToPay={daysToPay}
								editDaysToPay={(e) => editField("daysToPay", e)}
								firstDueDate={firstDueDate}
								loanDate={loanDate}
								type={type}
								dealId={dealId}
							/>
						</Col>
						{(leaseType === 5 || leaseType === 6) && (
							<Col xl="3" className="mb-2"></Col>
						)}

						{leaseType !== 6 && (
							<Col xl="3" className="mb-2">
								<LeaseAprInput />
							</Col>
						)}
						{leaseType === 5 && (
							<Col xl="3" className="mb-2">
								<MoneyFacInput />
							</Col>
						)}
						{leaseType === 6 && <MarkupInput />}

						<ResidualInput />
					</Row>

					{/* {(bankFee > 0 || taxType === 1) && (
						<Row>
							<Col xl="4" className="mb-2">
								<Label>Adj APR (%)</Label>
								<AdjustmentAPR />
							</Col>
							{bankFee > 0 && (
								<Col xl="6" className="mb-2">
									<Label>Prepaid Finance Charge</Label>
									<PrepaidFinanceCharge />
								</Col>
							)}
						</Row>
					)} */}

					<hr className="my-2" />

					<Row className="px-2">
						<Col xl="6" className="d-flex justify-content-end">
							<RollByPaymentButton />
						</Col>
						<Col xl="6" className="d-flex justify-content-end">
							<OpenModal
								BodyComponent={LeaseRollDriveOffModalBody}
								className="btn w-100 btn-md btn-primary"
								buttonText="Roll By DriveOff"
								size="lg"
							/>
						</Col>
					</Row>

					<hr className="my-2" />

					<Row className="px-2">
						<Col xl="12" className="d-flex justify-content-center">
							<OpenModalButton
								buttonText="Payment Options"
								onClick={togglePayment}
								iconClass="nc-icon nc-refresh-69"
								buttonClass="btn btn-sm btn-primary"
							/>
							<ChildrenModal
								modalTitle="Payment Options"
								modal={paymentOptions}
								toggle={togglePayment}
								size="lg"
							>
								<PaymentOptionsModal toggle={togglePayment} />
							</ChildrenModal>
						</Col>
					</Row>
				</Form>
			</CardBody>
		</>
	);
};

export default LeaseTerms;

/**
 * RollByPaymentButton component to handle the roll by payment functionality.
 *
 * @returns {JSX.Element} The rendered component
 */
const RollByPaymentButton = () => {
	const {
		rollBack,
		editPrice,
		rollPreValue,
		editRollPreValue,
		editRollBack,
		editTerm,
		editApr,
		editDown,
	} = useCurrentSaleStore((state) => state, shallow);
	const { editField: editLeaseField, leasePeriod, leaseType } = useCurrentLeaseStore(
		(state) => state,
		shallow
	);

	const { type, totalCost } = useCurrentDealStore(
		(state) => ({
			type: state.deal.type,
			totalCost: state.vehicle?.totalCost || 0,
		}),
		shallow
	);

	const confirmRollBack = () => {
		confirmDeleteMessage("Roll deal back?").then((res) => {
			if (res === "Roll deal back?") {
				handleRollBack();
			}
		});
	};

	const handleRollBack = () => {
		console.log(rollBack);
		console.log(Number.parseInt(rollBack));
		switch (Number.parseInt(rollBack)) {
			case 10:
				editLeaseField("price", rollPreValue);
				editPrice(rollPreValue);
				// For RTO: also restore markup when rolling back price
				if (leaseType === 6 && totalCost > 0) {
					const oldMarkup = rollPreValue - totalCost;
					editLeaseField("markup", oldMarkup);
				}
				break;
			case 20:
				editDown(rollPreValue);
				break;
			case 30:
				// Roll back APR and money factor
				const periodsPerYear = LoanPeriod[leasePeriod].term || 12;
				const mf = moneyFactorFromApr(rollPreValue, periodsPerYear);
				editApr(rollPreValue);
				editLeaseField("apr", rollPreValue);
				editLeaseField("moneyFactor", Number(mf.toFixed(8)));
				break;
			case 35:
				// Roll back Markup (RTO only)
				console.log("Rolling back markup to:", rollPreValue);
				const oldPrice = totalCost + rollPreValue;
				editLeaseField("markup", rollPreValue);
				editLeaseField("price", oldPrice);
				editPrice(oldPrice);
				break;
			case 40:
				console.log(rollPreValue);
				editTerm(rollPreValue);
				editLeaseField("term", rollPreValue);
				break;
			case 60:
				console.log(rollPreValue);
				editLeaseField("residualValue", rollPreValue);
				break;
			default:
				console.log("default" + rollPreValue);
				break;
		}
		editRollPreValue(0);
		editRollBack(0);
	};

	return rollBack === 0 || rollBack === null ? (
		<OpenModal
			BodyComponent={LeaseRollModalBody}
			className="btn w-100 btn-md btn-primary"
			buttonText="Roll By Payment"
			size="lg"
		/>
	) : (
		<Button
			readOnly={type !== DealStatus.PENDING}
			onClick={confirmRollBack}
			className="btn w-100 btn-md btn-primary"
		>
			Rollback
		</Button>
	);
};
